import {connectToParent} from "penpal";

const RELOAD_ATTEMPT_KEY = 'bavAppReloadAttempts';
const RELOAD_ATTEMPT_LIMIT = 2;
const WPP_OPEN_AUTH_ATTEMPT_KEY = 'wppOpenAuthAttempts';

export default function bavApp() {
    return {
        isDebug: document.body.dataset.environment !== 'production' && document.body.dataset.environment !== 'staging',
        appMode: document.body.dataset.appMode || 'standalone',
        contextLoaded: false,
        supportsThirdPartyCookies: true,
        contextChanged: false,

        async init() {
            if (this.isDebug) {
                console.log('🚀 Initializing global context manager');
            }

            // Reaching this component means we're past the login screen, so any
            // WPP Open auto-login attempt counter from the iframe login flow is
            // now stale and should be reset for the next session/login cycle.
            try {
                sessionStorage.removeItem(WPP_OPEN_AUTH_ATTEMPT_KEY);
            } catch (e) {
                // ignore
            }

            const previousAppMode = this.appMode;
            const detectedAppMode = window !== window.top ? 'embedded' : 'standalone';
            this.appMode = detectedAppMode;
            this.contextChanged = previousAppMode !== detectedAppMode;

            if (this.isDebug) {
                console.log('🔍 Context detected:', {
                    appMode: this.appMode,
                    previousAppMode,
                    changed: this.contextChanged,
                });
            }

            this.applyContextStyling();

            if (this.isEmbedded()) {
                await this.connectToParent();
            } else {
                this.markContextLoaded();
            }

            if (this.contextChanged) {
                await this.updateSessionContext();
                this.reloadIfWithinLimit();
            } else {
                this.clearReloadAttempts();
            }

            this.setupEventListeners();
        },

        isStandalone() {
            return this.appMode === 'standalone';
        },

        isEmbedded() {
            return this.appMode === 'embedded';
        },

        applyContextStyling() {
            document.body.classList.remove('app-standalone', 'app-embedded');

            if (this.isStandalone()) {
                document.body.classList.add('app-standalone');
            } else {
                document.body.classList.add('app-embedded');
            }

            // Set CSS custom property
            document.documentElement.style.setProperty('--app-context', this.isStandalone() ? 'standalone' : 'embedded');

            // Mark as detected
            document.body.classList.add('app-context-detected');
        },

        async connectToParent() {
            if (typeof connectToParent === 'undefined') {
                if (this.isDebug) {
                    console.warn('⚠️ Penpal not available');
                }
                this.markContextLoaded();
                return;
            }

            if (!this.supportsThirdPartyCookies) {
                if (this.isDebug) {
                    console.info('🍪 Third party cookies not supported');
                }
                this.markContextLoaded();
                return;
            }

            try {
                if (this.isDebug) {
                    console.log('🔗 Connecting to parent application...');
                }

                await connectToParent({
                    parentOrigin: '*',
                    debug: this.isDebug,
                    methods: {
                        receiveOsContext: (context) => {
                            if (this.isDebug) {
                                console.log('📥 OS Context received:', context);
                            }

                            this.handleOsContext(context);
                        }
                    }
                });

                if (this.isDebug) {
                    console.log('✅ Connected to parent application');
                }

                this.markContextLoaded();
            } catch (error) {
                if (this.isDebug) {
                    console.info('ℹ️ Not running inside supported parent application:', error.message);
                }
                this.markContextLoaded();
            }
        },

        async handleOsContext(context) {
            await this.updateSessionData('wpp_open_context', context);

            // Dispatch global event
            this.$dispatch('wpp-open-context-received', context);
        },

        async updateSessionContext() {
            await this.updateSessionData('app_context', {
                app_mode: this.appMode,
                updated_at: new Date().toISOString(),
            });
        },

        async updateSessionData(key, value) {

            if (this.isDebug) {
                console.log("Updating session data", {
                    key, value
                })
            }

            try {
                await fetch('/embed-manager/update-data', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                    },
                    body: JSON.stringify({
                        key: key,
                        value: value
                    })
                });
            } catch (error) {
                if (this.isDebug) {
                    console.warn('⚠️ Failed to update session:', error);
                }
            }
        },

        reloadIfWithinLimit() {
            const attempts = this.getReloadAttempts();

            if (attempts >= RELOAD_ATTEMPT_LIMIT) {
                console.warn(`⚠️ bavApp reload limit reached (${attempts}); skipping further reloads to prevent a loop.`);
                this.clearReloadAttempts();
                return;
            }

            try {
                sessionStorage.setItem(RELOAD_ATTEMPT_KEY, String(attempts + 1));
            } catch (e) {
                // sessionStorage may be unavailable (private mode, etc.) — fail open to a single reload.
            }

            if (this.isDebug) {
                console.log(`🔄 Context changed, reloading (attempt ${attempts + 1}/${RELOAD_ATTEMPT_LIMIT})`);
            }

            window.location.reload();
        },

        getReloadAttempts() {
            try {
                return parseInt(sessionStorage.getItem(RELOAD_ATTEMPT_KEY) || '0', 10) || 0;
            } catch (e) {
                return 0;
            }
        },

        clearReloadAttempts() {
            try {
                sessionStorage.removeItem(RELOAD_ATTEMPT_KEY);
            } catch (e) {
                // ignore
            }
        },

        markContextLoaded() {
            this.contextLoaded = true;

            // Hide loading overlay
            const overlay = this.$refs.overlay;

            if (overlay) {
                overlay.style.display = 'none';
            }

            if (this.isDebug) {
                console.log('✅ Context fully loaded and initialized');
            }
        },

        setupEventListeners() {
            document.addEventListener('livewire:navigated', () => {
                // Re-apply context styling after navigation
                this.applyContextStyling();
            });
        }
    }
}
